/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import HealthEntryNameText from './HealthEntryNameText';
import ShareCardIncludedTypeNames from '../../constants/ShareCardIncludedTypeNames';
import MedliAmountText from './MedliAmountText';

import {
  listDateFormatHeValue,
} from '../../models/HealthEntryTextFormatting';
import GeneVariantResultText from './GeneVariantResultText';
import HealthEntrySummaryTemplate from './HealthEntrySummaryTemplate';
import GeneAttributesListResultText from './GeneAttributesListResultText';
import { useHealthEntryValueForActiveProfile } from '../hooks/useHealthEntry';
import { DateTimeModel } from '../../models/DateTimeModel';
import { getRelativeDateValue } from '../localization/LocalizedDate';
import { iObservation, ObservationModel } from '../../apiServices/apiModels/Trackable';
import FlexRow from './FlexRow';
import HealthEntryMetaIcons from '../views/components/attributes/HealthEntryMetaIcons';

const GeneAdditionalAttributes = (props) => {
  const { healthEntryValue, languageCode } = props;
  const { geneVariantResult } = healthEntryValue;

  return (
    <GeneAttributesListResultText
      className="font-footnote cmp-hesummary-gene-attributes"
      geneVariantResult={geneVariantResult}
      languageCode={languageCode}
    />
  );
};

interface Props {
  healthEntryId?: number;
  heValueId: number;
  color: string;
  includeName: boolean;
  languageCode: string;
  areIconsAttachedToRight: boolean;
}

const HealthEntryValueSummary = (props: Props) => {
  const { heValueId, languageCode, color, includeName, areIconsAttachedToRight } = props;

  const [{ isLoaded }, hevActions] = useHealthEntryValueForActiveProfile(heValueId);

  if (!isLoaded) { return null; }

  const healthEntry = hevActions.instantiateHealthEntry();
  const healthEntryValue = hevActions.instantiateHealthEntryValue();

  const amount = healthEntryValue.amount && healthEntryValue.amount.get();
  const geneVariantResult = healthEntryValue.geneVariantResult;

  return (
    <HealthEntrySummaryTemplate
      color={color}
      title={
        <div>
          {includeName && <HealthEntryNameText healthEntry={healthEntry} includedValues={ShareCardIncludedTypeNames.all} />}
          {(includeName && amount) && ': ' }
          {amount && <MedliAmountText amount={amount} description={healthEntryValue.description.get()} />}
          {(amount && geneVariantResult) && ': ' }
          {geneVariantResult && <GeneVariantResultText geneVariantResult={geneVariantResult.get()} />}
        </div>
      }
      additionalTitle={(
        geneVariantResult
        ? (
          <GeneAdditionalAttributes
            healthEntryValue={healthEntryValue}
            languageCode={languageCode}
          />
        ) : null
      )}
      subTitle={listDateFormatHeValue(healthEntryValue)}
      areIconsAttachedToRight={areIconsAttachedToRight}
      hasAttachments={healthEntryValue.attachments.hasAttachments()}
      hasComments={healthEntryValue.comments.hasComments()}
      isFlagged={healthEntryValue.flag.isFlagged()}
    />
  );
};

const DateTimeSummary = injectIntl((props) => {
  const { date, languageCode, intl } = props;

  /*
    "precision": "P1DT",
		"date": "2020-08-10",
		"time": "12:13:14",
		"timezone": "America/New_York"
  */
  const summaryText = [];
  const dateTime = new DateTimeModel(date, intl);

  if (dateTime.hasDate()) {
    summaryText.push(getRelativeDateValue(intl, date.date, date.precision, 'medium', false, languageCode));
  }
  if (dateTime.hasTime()) {
    summaryText.push(dateTime.formatTime());
  }

  return summaryText.length > 0 ? (
    <div>
      {summaryText.join(' – ')}
    </div>
  ) : (
    <div>
      <FormattedMessage id="ui-data.date.not-specified.text" />
    </div>
  );
});

const RenderObservationIcons = (props) => {
  const { observation } = props;
  const o = new ObservationModel(observation, '');

  return (
    <HealthEntryMetaIcons
      hasAttachments={o.hasAttachments}
      hasComments={o.hasComments}
      isFlagged={o.isFlagged}
      style={{ marginRight: 4 }}
    />
  );
};

interface iObservationSummaryProps {
  observation: iObservation;
  languageCode: string;
}
export const ObservationSummary: React.FC<iObservationSummaryProps> = (props) => {
  const { observation, languageCode } = props;

  const { attributes } = observation;
  const summary = observation.summary[languageCode] || '';
  const hasAttributes = attributes.length > 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="font-body font-color-gsDarkest" >
          {
            hasAttributes
              ? summary
              : <DateTimeSummary date={observation.date} />
          }
        </div>
        <RenderObservationIcons observation={observation} />
      </div>
      {
        hasAttributes ? (
          <div className="font-footnote font-color-gsDarkest" style={{ marginTop: 4 }}>
            <DateTimeSummary date={observation.date} />
          </div>
        ) : null
      }
    </div>
  );
};

export default HealthEntryValueSummary;
